import React, { useEffect, useState } from 'react';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import DataTableBody from './DataTableBody/DataTableBody';
import DataTableHeader from './DataTableHeader/DataTableHeader';
import PropTypes from 'prop-types';
import DataTableToolbar from './DataTableToolbar/DataTableToolbar';
import DataTablePagination from './DataTablePagination/DataTablePagination';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import styled from 'styled-components';

/**
 * Component for table data displaying
 *
 * @param {boolean} displayToolbar
 * @param {boolean} displayHeader
 * @param {boolean} displayPagination
 * @param {boolean} lineHover Highlight row on hover
 * @param {function} onLineClick
 * @param {array} columns Object array with columns descriptions
 * @param {number} totalCount Total row number - displayed with pagination
 * @param {array} rowsPerPageOptions Array of options for pagination
 * @param {array} data Object array with data for table body
 * @param {string} error
 * Error string which is displayed instead of data (for dynamic data loading)
 * @param {boolean} loading
 * Display loading indicator instead of data (for dynamic data loading)
 * @param {string} fulltextFilter Fulltext filter value
 * @param {function} setFulltextFilter Fulltext filter setter
 * @param {object|null} columnsFilter
 * @param {function} setColumnsFilter
 * @param {string} orderDirection
 * @param {function} setOrderDirection
 * @param {string} orderByColumn
 * @param {function} setOrderByColumn
 * @param {number} page
 * @param {function} setPage
 * @param {number} rowsPerPage
 * @param {function} setRowsPerPage
 * @param {node} customTools
 */

const DataTableLoaderWrapper = styled.div`
  height: 2px;
`;

const StyledTableContainer = styled(TableContainer)`
  max-height: 52vh
`;

const DataTable = ({
  displayToolbar = true,
  displayHeader = true,
  displayPagination = true,
  displaySearch = true,
  checkboxColumn = false,
  lineHover = false,
  onLineClick = () => {},
  columns,
  totalCount,
  rowsPerPageOptions,
  data,
  error,
  loading,
  fulltextFilter = '',
  setFulltextFilter = () => {},
  columnsFilter = null,
  setColumnsFilter = () => {},
  orderDirection = '',
  setOrderDirection = () => {},
  orderByColumn = '',
  setOrderByColumn = () => {},
  page = 0,
  setPage = () => {},
  rowsPerPage = 10,
  setRowsPerPage = () => {},
  customTools = null,
  checkboxTools = null,
  onFilterOpen = () => {},
}) => {
  const [checkboxLines, setCheckboxLines] = useState([]);
  const [checkedAll, setCheckedAll] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [fulltextFilter, columnsFilter, rowsPerPage, setPage]);

  useEffect(() => {
    let checked = [...checkboxLines];
    data.forEach((line) => {
      const index = checked.indexOf(line.id);
      if (checkedAll) {
        if (index === -1) {
          checked.push(line.id);
        }
      } else {
        if (index !== -1) {
          checked = checked.filter((item, key) => key !== index);
        }
      }
    });
    setCheckboxLines(checked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedAll]);

  return (
    <div>
      <DataTableLoaderWrapper>{loading && <LoadingIndicator />}</DataTableLoaderWrapper>
      {displayToolbar && (
        <DataTableToolbar
          search={displaySearch}
          fulltextFilter={fulltextFilter}
          setFulltextFilter={setFulltextFilter}
          checkboxLines={checkboxLines}
          checkboxTools={checkboxTools}
          customTools={customTools}
        />
      )}
      <StyledTableContainer>
        <Table stickyHeader size={'small'}>
          {displayHeader && (
            <DataTableHeader
              showCheckbox={checkboxColumn}
              columns={columns}
              order={orderDirection}
              orderBy={orderByColumn}
              columnsFilter={columnsFilter}
              setOrder={setOrderDirection}
              setOrderBy={setOrderByColumn}
              setColumnsFilter={setColumnsFilter}
              checkboxLines={checkboxLines}
              setCheckboxLines={setCheckboxLines}
              checkedAll={checkedAll}
              setCheckedAll={setCheckedAll}
              onFilterOpen={onFilterOpen}
            />
          )}
          <DataTableBody
            showCheckbox={checkboxColumn}
            rowsPerPage={rowsPerPage}
            columns={columns}
            data={data || []}
            loading={loading}
            error={error}
            lineHover={lineHover}
            onLineClick={onLineClick}
            checkboxLines={checkboxLines}
            setCheckboxLines={setCheckboxLines}
            checkedAll={checkedAll}
            setCheckedAll={setCheckedAll}
          />
        </Table>
      </StyledTableContainer>
      {displayPagination && (
        <DataTablePagination
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onChangePage={(event, currentPage) => setPage(currentPage)}
          onChangeRowsPerPage={(event) => {
            setRowsPerPage(event.target.value);
            setPage(0);
          }}
        />
      )}
    </div>
  );
};

DataTable.propTypes = {
  displayToolbar: PropTypes.bool,
  displayHeader: PropTypes.bool,
  displayPagination: PropTypes.bool,
  displaySearch: PropTypes.bool,
  lineHover: PropTypes.bool,
  onLineClick: PropTypes.func,
  columns: PropTypes.array,
  totalCount: PropTypes.number,
  rowsPerPageOptions: PropTypes.array,
  data: PropTypes.array,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  loading: PropTypes.bool,
  checkboxColumn: PropTypes.bool,
  checkboxTools: PropTypes.array,
  fulltextFilter: PropTypes.string,
  setFulltextFilter: PropTypes.func,
  columnsFilter: PropTypes.object,
  setColumnsFilter: PropTypes.func,
  orderDirection: PropTypes.string,
  setOrderDirection: PropTypes.func,
  orderByColumn: PropTypes.string,
  setOrderByColumn: PropTypes.func,
  page: PropTypes.number,
  setPage: PropTypes.func,
  rowsPerPage: PropTypes.number,
  setRowsPerPage: PropTypes.func,
  customTools: PropTypes.node,
  onFilterOpen: PropTypes.func,
};

export default DataTable;
